import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { LinearProgress, SxProps, styled } from '@mui/material';
import AgGridReact from 'ui/AgGridReact/AgGridReact';
import {
    ColDef,
    ColGroupDef,
    Column,
    ColumnRowGroupChangedEvent,
    ColumnVisibleEvent,
    DomLayoutType,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
} from 'ag-grid-community';
import { AgGridReactProps } from 'ag-grid-react';
import LoadingData from 'routes/HumanResourcesModule/components/LoadingGrid';
import { StyGridContainer } from 'routes/HumanResourcesModule/components/ui/StyGridContainer';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import ExportButton from 'components/ExportButton/ExportButton';
import isEqual from 'lodash/isEqual';
import { getDefaultGridProps } from './helpers/gridHelpers';
import NoData from './NoData';
import { Theme } from '@mui/system';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import { HUMAN_RESOURCES } from 'business/constants';
import {
    getCsvExportFilename,
    getExcelExportFilename,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { getDefaultAgentGridExportParams } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/gridHelpers';

type Props = {
    title: string;
    infoTooltip?: string;
    rowData: Array<unknown>;
    columnDefs: Array<ColDef | ColGroupDef>;
    exportFileName: string;
    exportSheetName?: string;
    isLoading: boolean;
    isFetching?: boolean;
    showTotal?: boolean;
    hasFullScreenMode?: boolean;
    gridProps?: AgGridReactProps;
    cardHeaderActions?: React.ReactNode;
    cardFooterActions?: React.ReactNode;
    titleNode?: React.ReactNode;
    noDataNode?: React.ReactNode;
    wrapperComponent?: React.ComponentType<{ children: React.ReactElement }>;
    onGridReady?: (event: GridReadyEvent) => void;
    className?: string;
    sx?: SxProps<Theme>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    trackEventMetadata?: Record<string, any>;
};

const isNoData = (data: Array<unknown>) => !data || data.length === 0;

const HrDataGrid = ({
    title,
    infoTooltip,
    rowData,
    columnDefs,
    cardHeaderActions,
    cardFooterActions,
    isLoading,
    isFetching,
    hasFullScreenMode = true,
    showTotal = true,
    gridProps,
    trackEventMetadata = undefined,
    exportFileName,
    exportSheetName,
    titleNode,
    noDataNode,
    wrapperComponent: WrapperComponent,
    onGridReady: propsOnGridReady = undefined,
    className = undefined,
    sx = undefined,
}: Props): React.ReactElement => {
    const gridApiRef = React.useRef<GridApi | null>(null);
    const [gridApi, setGridApi] = React.useState<GridApi | null>(null);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [sidebarState, setSidebarState] = React.useState<Column[] | null>(
        null,
    );
    const resizeColumns = useCallback(() => {
        // this hack is a bit flaky, in very large screen 8 columns  might not take the grid's entire width
        // a better handling should be applied
        if (gridApi && columnDefs) {
            const allCols = gridApi.getAllDisplayedColumns();
            if (allCols && allCols.length <= 8) {
                gridApi.sizeColumnsToFit();
            }
        }
    }, [gridApi, columnDefs]);

    const onGridReady = useCallback((event: GridReadyEvent) => {
        gridApiRef.current = event.api;
        setGridApi(event.api);
        resizeColumns();
        propsOnGridReady?.(event);
    }, []);

    const onColumnRowGroupChanged = useCallback(
        (event: ColumnRowGroupChangedEvent) => {
            if (event.columns) {
                // save the state of the row group to persist it when rerendering
                setSidebarState(event.columns);
            }
        },
        [],
    );

    useEffect(() => {
        if (gridApi && columnDefs) {
            // in case there is just only 1 month to display, the column is resized
            // to fill the remaning available space
            // the default configuration provides 4 columns when 1 month is displayed
            resizeColumns();
            // Keep the row group through rerendering
            if (gridApiRef.current && sidebarState) {
                requestAnimationFrame(() => {
                    gridApi.setRowGroupColumns(sidebarState);
                });
            }
        }
    }, []);

    // Reset floating filter of the column if it is hidden
    const onColumnVisible = useCallback(
        (event: ColumnVisibleEvent) => {
            const filterModel = gridApi?.getFilterModel();
            const coldId = event.column?.getColId();
            if (filterModel) {
                const gridFloatingFilters = Object.keys(filterModel);
                if (
                    !event.visible &&
                    coldId &&
                    gridFloatingFilters.includes(coldId)
                ) {
                    gridApi?.destroyFilter(coldId);
                }
            }
        },
        [gridApi],
    );

    const defaultGridProps = getDefaultGridProps();
    const exportOptions = {
        allColumns: true,
        fileName: getExcelExportFilename(exportFileName),
        sheetName: exportSheetName,
    };

    const customGridProps = useMemo(() => {
        const domLayout: DomLayoutType = isFullScreen ? 'normal' : 'autoHeight';
        return {
            ...defaultGridProps,
            ...gridProps,
            defaultCsvExportParams: {
                ...getDefaultAgentGridExportParams(),
                ...gridProps?.defaultCsvExportParams,
                fileName: getCsvExportFilename(exportFileName),
            },
            defaultExcelExportParams: {
                ...getDefaultAgentGridExportParams(),
                ...gridProps?.defaultExcelExportParams,
                fileName: getExcelExportFilename(exportFileName),
            },

            pagination: gridProps?.pagination ?? !isFullScreen,
            domLayout, // Assign the type-safe domLayout
        };
    }, [
        JSON.stringify(defaultGridProps),
        JSON.stringify(gridProps),
        isFullScreen,
    ]);

    const getRowId = useCallback(
        (params: GetRowIdParams) => Object.values(params.data).join('-'),
        [],
    );
    const headerActions = (
        <>
            {cardHeaderActions}
            <ExportButton exportOptions={exportOptions} gridApi={gridApi} />
        </>
    );

    const Wrapper: React.ComponentType<{ children: React.ReactNode }> =
        useMemo(() => {
            return WrapperComponent
                ? WrapperComponent
                : ({ children }: { children: React.ReactElement }) => (
                      <>{children}</>
                  );
        }, [WrapperComponent]);

    const content = useMemo(() => {
        if (isLoading) {
            return <LoadingData />;
        }
        if (isNoData(rowData)) {
            return noDataNode ? noDataNode : <NoData />;
        }
        return (
            <StyGridContainer
                className={className}
                sx={{ ...sx, height: '100%', width: '100%' }}
            >
                <Wrapper>
                    <AgGridReact
                        {...customGridProps}
                        getRowId={getRowId}
                        enableBrowserTooltips
                        tooltipShowDelay={0}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        onGridReady={onGridReady}
                        onColumnRowGroupChanged={onColumnRowGroupChanged}
                        onColumnVisible={onColumnVisible}
                        grandTotalRow={showTotal ? 'bottom' : undefined}
                    />
                </Wrapper>
            </StyGridContainer>
        );
    }, [
        isLoading,
        rowData,
        customGridProps,
        getRowId,
        columnDefs,
        onGridReady,
        onColumnVisible,
        onColumnRowGroupChanged,
    ]);

    const cardContent = (
        <HrCard
            title={title}
            infoTooltip={infoTooltip}
            startAdornment={hasFullScreenMode && <ConditionnalZoomIconButton />}
            cardHeaderActions={headerActions}
            cardFooterActions={cardFooterActions}
            titleNode={titleNode}
            setIsFullScreen={setIsFullScreen}
        >
            {isFetching && !isLoading && <LinearProgress color="primary" />}
            {content}
        </HrCard>
    );
    if (!hasFullScreenMode) {
        return <>{cardContent}</>;
    }

    return (
        <FullScreenProvider
            module={HUMAN_RESOURCES}
            trackEventMetadata={trackEventMetadata}
        >
            {cardContent}
        </FullScreenProvider>
    );
};

export const StyHrDataGrid = styled(HrDataGrid, {
    name: 'StyHrDataGrid',
})({
    '& .ag-row-footer': {
        textTransform: 'uppercase',
        fontWeight: 'bold',
    },
});

export default memo(HrDataGrid, isEqual);
